import React, {useEffect, useState, useMemo, useRef} from "react";
import {inject, observer} from "mobx-react";
import {useNavigate} from "react-router-dom";
import {useTranslation} from "react-i18next";
import debounce from "lodash.debounce";
//local
import {Layout} from "components/UI/layout";
import {ButtonTransparent} from "components/buttons/ButtonTransparent";
import MiniSelect from "components/selects/MiniSelect";
import {IconBriefcase, IconPlus} from "components/icons";
import {ProjectModal} from "components/modals/ProjectModal";
import {Tabs} from "components/Tabs";
import {projectsTabs, projectTabKeys} from "constants/tabs.const";
import {hasPermission} from "helpers/helper";
import {ConfirmModal} from "components/ConfirmModal";
import {hashes} from "constants/tabs.const";
import {DecorativeSearch} from "components/Filters/DecorativeSearch";
import {useSavedFilters} from "helpers/userHooks";

const OurProjectsPage = inject("store")(
  observer(({store: {auth, buyer, organization}}) => {
    const {user} = auth;
    const {projects, projectFilterParams} = buyer;
    const {departments, projectsDropdownDepartments} = organization;
    const {t} = useTranslation();
    const navigate = useNavigate();
    const [isProjectModal, setIsProjectModal] = useState(null);
    const searchInputRef = useRef("");

    const defaultActiveProjectsTab = hasPermission(["ENABLE_PROJECTS_MINE_DEFAULT_TAB"])
      ? projectTabKeys.MINE
      : projectTabKeys.ALL;

    const defaultFilters = {
      search: null,
      page: 1,
      tab: defaultActiveProjectsTab,
      departmentId: null,
    };
    const validateSearch = (search) =>
      search && search !== "undefined" && search !== "null" ? search : null;

    const [filters, setFilters] = useSavedFilters({
      key: "projectsPageFilters",
      defaultValue: defaultFilters,
      allowedValues: {tab: ["mine", "all", "archived"]},
      allowedKeys: ["search", "page", "tab", "departmentId"],
      updateFilters: (values) => buyer.setProjectFilterParams(values),
      storage: "local",
    });

    const activeProjectsTab = useMemo(() => {
      return projectsTabs.find(
        (tab) => tab.requestKey === (filters?.tab ?? defaultActiveProjectsTab)
      )?.id;
    }, [filters, defaultActiveProjectsTab]);
    const isNeedNewProjectButton = useMemo(
      () =>
        filters?.tab === projectTabKeys.ARCHIVED
          ? false
          : !!projects?.rows?.length || !!projects?.totalItems,
      [filters, projects]
    );
    const handleClickNewProject = () =>
      hasPermission(["CREATE_PROJECT"])
        ? setIsProjectModal("new-project")
        : ConfirmModal({
            title: t("WANT_START_PROJECT"),
            text: t("CANT_START_PROJECT"),
            okText: "READ_AND_UPGRADE",
            type: "warning",
            onOk: () => navigate({hash: hashes.SUBSCRIPTION}),
          });

    const handleClose = (isUpdateProject) => {
      setIsProjectModal(null);
      if ([projectTabKeys.MINE, projectTabKeys.ALL].includes(isUpdateProject))
        setFilters({...filters, tab: isUpdateProject});
      if (typeof isUpdateProject === "boolean") buyer.getProjects(isUpdateProject);
    };

    const handleSearch = debounce(() => {
      const search = searchInputRef.current?.value?.trim();
      const validatedSearch = validateSearch(search);
      if (validatedSearch) setFilters({...filters, search: validatedSearch, page: 1});
      else setFilters({...filters, search: null});
    }, 800);

    useEffect(() => {
      !!filters &&
        organization.getOrganizationDepartmentLabels().then((data) => {
          filters.departmentId &&
            !data?.find((item) => item.id === filters.departmentId) &&
            setFilters({...filters, departmentId: null});
        });
      if (!!filters && !!filters?.search) searchInputRef.current.value = filters?.search;

      return () => organization.setOrganizationDepartmentLabels(null);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [organization, !!filters]);

    useEffect(() => {
      if (!!filters && projectFilterParams?.page !== filters?.page) {
        setFilters({...filters, page: projectFilterParams?.page});
      }
    }, [filters, projectFilterParams?.page, filters?.page, setFilters]);

    return (
      <Layout>
        <div className="our-projects">
          <div className="content-block_header">
            <div className="responsive-wrapper">
              <div className="responsive-unit">
                <IconBriefcase />
                <h3 className="content-block_title __uppercase">{t("PROJECTS")}</h3>
              </div>
              <div className="responsive-unit">
                <DecorativeSearch
                  searchFocus={searchInputRef}
                  searchOnChange={handleSearch}
                  searchPlaceholder={t("SEARCH_PROJECTS")}
                  showSearch
                  hideFilterParts={false}
                  hideEntireFilter={false}
                  filterToggle={true}
                  isFilterVisible={!!filters?.search}
                  showClearIcon
                  onClearSearch={() =>
                    setFilters({...filters, search: undefined, page: 1})
                  }
                />
                {!!departments && user.ActiveOrganization?.useDepartmentsForProjects && (
                  <MiniSelect
                    list={projectsDropdownDepartments}
                    defaultValue={filters?.departmentId ?? null}
                    addClass="ml-4"
                    itemAsObject
                    onChange={(value) => {
                      setFilters({
                        ...filters,
                        departmentId: value,
                        page: 1,
                      });
                    }}
                    label={projects?.rows?.length ?? 0}
                    tinyLabel
                    boldLabel
                  />
                )}
              </div>
            </div>
            {isNeedNewProjectButton && (
              <ButtonTransparent
                addClass="our-projects_head__btn"
                name="NEW_PROJECT"
                type="button"
                icon={<IconPlus />}
                onClick={handleClickNewProject}
              />
            )}
          </div>
          <Tabs
            tabs={projectsTabs}
            active={activeProjectsTab}
            setActive={(id) => {
              setFilters({
                ...filters,
                tab: projectsTabs.find((tab) => tab.id === id)?.requestKey,
                page: 1,
              });
            }}
            addClass="our-projects_content"
          />
          {isProjectModal && (
            <ProjectModal projectId={isProjectModal} onClose={handleClose} />
          )}
        </div>
      </Layout>
    );
  })
);

export default OurProjectsPage;
